import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER, XLSX } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError, interpretDataIntoXlsxFormat } from "../helpers";
import { IServiceResource } from "./servicesResourcesReducer";
import { tryConfirmLogin } from "./authReducer";
import xlsx from "json-as-xlsx";

class ExportServicesErrs {
    form: string | null = null;
}

export interface IService {
    _id: string;
    name: string;
    resource: IServiceResource;
    cost?: number;
    repeated?: boolean;
    driverCanSee: boolean;
}

class RegServiceErrs {

    index?: any;

    form: string | null = null;
    name: string | null = null;
    serviceResourceId: string | null = null;
    cost: string | null = null;
}

export interface IServicesState {
    registeringService: boolean;
    registeredService: boolean;
    regServiceErrs: RegServiceErrs;
    fetchingServices: boolean;
    services: IService[];
    fetchServicesErr: string | null;
    mainService: IService | null;
    updatingService: boolean;
    updatedService: boolean;
    updateServiceErrs: RegServiceErrs;
    exportingServices: boolean | null;
    exportedServices: boolean | null;
    exportServicesErrs: ExportServicesErrs;
    importingServices: boolean;
    triedImportingServices: boolean | null;
    importedServices: boolean | null;
    importServicesErrs: RegServiceErrs[];
}

const initialState = {
    registeringService: false,
    registeredService: false,
    regServiceErrs: new RegServiceErrs(),
    fetchingServices: false,
    services: [],
    fetchServicesErr: null,
    mainService: null,
    updatingService: false,
    updatedService: false,
    updateServiceErrs: new RegServiceErrs(),
    exportingServices: false,
    exportedServices: false,
    exportServicesErrs: new ExportServicesErrs(),
    triedImportingServices: false,
    importingServices: false,
    importedServices: false,
    importServicesErrs: [],
} as IServicesState;

const servicesSlice = createSlice({
    name: 'services',
    initialState,
    reducers: {
        clearRegServiceFieldErr: (state: IServicesState, action: PayloadAction<string>) => {
            state.regServiceErrs[action.payload as keyof RegServiceErrs] = null;
        },
        clearRegServiceErrs: (state: IServicesState) => {
            state.regServiceErrs = new RegServiceErrs();
        },
        tryResetRegServiceFormErr: (state: IServicesState) => {
            let shouldReset = true;

            for (let key in state.regServiceErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regServiceErrs[key as keyof RegServiceErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regServiceErrs.form = null;
            }
        },
        resetRegisteredService: (state: IServicesState) => {
            state.registeredService = false;
        },
        regServiceStart: (state: IServicesState) => {
            state.regServiceErrs = new RegServiceErrs();
            state.registeredService = false;
            state.registeringService = true;
        },
        regServicePass: (state: IServicesState) => {
            state.registeringService = false;
            state.registeredService = true;
        },
        regServiceFail: (state: IServicesState, action: PayloadAction<IFieldError[]>) => {
            state.registeringService = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regServiceErrs) {
                    if (err.field === key) {
                        state.regServiceErrs[key as keyof RegServiceErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regServiceErrs.form) {
                state.regServiceErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchServicesErr: (state: IServicesState) => {
            state.fetchServicesErr = null;
        },
        setMainService: (state: IServicesState, action: PayloadAction<IService | null>) => {
            state.mainService = action.payload;
        },
        fetchServicesStart: (state: IServicesState) => {
            state.fetchServicesErr = null;
            state.fetchingServices = true;
        },
        fetchServicesPass: (state: IServicesState, action: PayloadAction<IService[]>) => {
            state.fetchingServices = false;

            state.services = action.payload;
        },
        fetchServicesFail: (state: IServicesState, action: PayloadAction<string>) => {
            state.fetchingServices = false;
            state.fetchServicesErr = action.payload;
        },
        clearUpdateServiceFieldErr: (state: IServicesState, action: PayloadAction<string>) => {
            state.updateServiceErrs[action.payload as keyof RegServiceErrs] = null;
        },
        clearUpdateServiceErrs: (state: IServicesState) => {
            state.updateServiceErrs = new RegServiceErrs();
        },
        tryResetUpdateServiceFormErr: (state: IServicesState) => {
            let shouldReset = true;

            for (let key in state.updateServiceErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateServiceErrs[key as keyof RegServiceErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateServiceErrs.form = null;
            }
        },
        resetUpdatedService: (state: IServicesState) => {
            state.updatedService = false;
        },
        updateServiceStart: (state: IServicesState) => {
            state.updatedService = false;
            state.updateServiceErrs = new RegServiceErrs();
            state.updatingService = true;
        },
        updateServicePass: (state: IServicesState, action: PayloadAction<IService>) => {
            state.updatingService = false;
            state.updatedService = true;
        },
        updateServiceFail: (state: IServicesState, action: PayloadAction<IFieldError[]>) => {
            state.updatingService = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateServiceErrs) {
                    if (err.field === key) {
                        state.updateServiceErrs[key as keyof RegServiceErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateServiceErrs.form) {
                state.updateServiceErrs.form = 'Form incorrectly filled.';
            }
        }, clearExportServicesFieldErr: (state: IServicesState, action: PayloadAction<string>) => {
            state.exportServicesErrs[action.payload as keyof ExportServicesErrs] = null;
        },
        clearExportServicesErrs: (state: IServicesState) => {
            state.exportServicesErrs = new ExportServicesErrs();
        },
        tryResetExportServicesFormErr: (state: IServicesState) => {
            let shouldReset = true;

            for (let key in state.exportServicesErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.exportServicesErrs[key as keyof ExportServicesErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.exportServicesErrs.form = null;
            }
        },
        exportServicesStart: (state: IServicesState) => {
            state.exportingServices = true;
            state.exportedServices = false;
        },
        exportServicesPass: (state: IServicesState) => {
            state.exportingServices = false;
            state.exportedServices = true;
        },
        exportServicesFail: (state: IServicesState, action: PayloadAction<IFieldError[]>) => {
            state.exportingServices = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.exportServicesErrs) {
                    if (err.field === key) {
                        state.exportServicesErrs[key as keyof ExportServicesErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.exportServicesErrs.form) {
                state.exportServicesErrs.form = 'Form incorrectly filled.';
            }
        },
        clearImportServicesFieldErr: (state: IServicesState, action: PayloadAction<string>) => {
            state.importServicesErrs = [];
        },
        clearImportServicesErrByIndex: (state: IServicesState, action: PayloadAction<number>) => {

            const index = action.payload;

            let newErrs = [];

            if (index < state.importServicesErrs.length) {
                for (let i = 0; i < index; i++) {
                    newErrs[i] = state.importServicesErrs[i];
                }

                for (let i = index; i < (state.importServicesErrs.length - 1); i++) {
                    newErrs[i] = (state.importServicesErrs)[i + 1];
                }

                state.importServicesErrs = newErrs;
            }
        },
        clearResetTriedImportingServices: (state: IServicesState) => {
            state.triedImportingServices = false;
        },
        clearImportServicesErrs: (state: IServicesState) => {
            state.importServicesErrs = [];
        },
        tryResetImportServicesFormErr: (state: IServicesState) => {

        },
        importServicesStart: (state: IServicesState) => {

            state.importServicesErrs = [];

            state.importingServices = true;
            state.triedImportingServices = false;
            state.importedServices = false;

        },
        importServicesDone: (state: IServicesState) => {
            state.importingServices = false;
            state.triedImportingServices = true;
        },
        importServicesPass: (state: IServicesState) => {
            state.importingServices = false;
            state.triedImportingServices = true;
            state.importedServices = true;
        },
        addErr2ImportServicesErrs: (state: IServicesState, action: PayloadAction<RegServiceErrs>) => {

            let errs = state.importServicesErrs;

            errs = [...errs, action.payload];

            state.importServicesErrs = errs;
        },
        importServicesFail: (state: IServicesState) => {
            state.importingServices = false;
        }
    }
});

export const {
    clearRegServiceErrs,
    clearRegServiceFieldErr,
    tryResetRegServiceFormErr,
    resetRegisteredService,
    regServiceStart,
    regServicePass,
    regServiceFail,
    clearFetchServicesErr,
    fetchServicesStart,
    fetchServicesPass,
    fetchServicesFail,
    setMainService,
    clearUpdateServiceErrs,
    clearUpdateServiceFieldErr,
    tryResetUpdateServiceFormErr,
    resetUpdatedService,
    updateServiceStart,
    updateServicePass,
    updateServiceFail,
    clearExportServicesErrs,
    clearExportServicesFieldErr,
    tryResetExportServicesFormErr,
    exportServicesStart,
    exportServicesPass,
    exportServicesFail,
    clearResetTriedImportingServices,
    clearImportServicesErrs,
    clearImportServicesFieldErr,
    tryResetImportServicesFormErr,
    importServicesStart,
    importServicesDone,
    importServicesPass,
    addErr2ImportServicesErrs,
    importServicesFail
} = servicesSlice.actions;

interface ITryRegServiceArg {
    data: {
        name: string;
        serviceResourceId?: string;
        cost?: string;
        driverCanSee: boolean
    };
    authToken: string;
}

export const tryRegService = createAsyncThunk('services/tryRegService', (arg: ITryRegServiceArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/service`;

    const { data, authToken } = arg;

    dispatch(regServiceStart());

    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(regServicePass());
    }).catch(err => {

        const data = err?.response?.data;


        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regServiceFail(fieldErrors));
    })
});

interface ITryFetchServicesArg {
    authToken: string;
}

export const tryFetchServices = createAsyncThunk('services/tryFetchServices', (arg: ITryFetchServicesArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services`;

    const { authToken } = arg;

    dispatch(fetchServicesStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchServicesPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }
        let error = '';

        if (data?.err && typeof data?.err === typeof new String('')) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchServicesFail(error));
    });
});

interface ITryUpdateServiceArg {
    serviceId: string;
    data: {
        name?: string;
        serviceResourceId?: string;
        cost?: string;
    };
    authToken: string;
}

export const tryUpdateService = createAsyncThunk('services/tryUpdateService', (arg: ITryUpdateServiceArg, { dispatch }) => {

    const { serviceId, data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services/${serviceId}`;

    dispatch(updateServiceStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateServicePass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateServiceFail(fieldErrors));
    });
});

interface ITryExportServicesArg {
    format: string;
    authToken: string;
}

export const tryExportServices = createAsyncThunk("servicesPlans/tryExportServices", (arg: ITryExportServicesArg, { dispatch }) => {

    const { authToken, format } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services-export`;

    dispatch(exportServicesStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        if (data.length === 0) {
            const fieldErrors = [{ field: 'form', msg: 'Export Empty.' }];

            dispatch(exportServicesFail(fieldErrors));
        } else {

            switch (format) {
                case XLSX:
                default:
                    const fileSettings = {
                        fileName: "Services"        //TODO: dynamically generate the name, based on the parameters used to fetch the data
                    };

                    try {
                        xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
                    } catch (err) {
                        alert("err");
                        console.log(err);
                    }
                    break;
            }

            dispatch(exportServicesPass());
        }
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            console.log(data);
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(exportServicesFail(fieldErrors));
    })


});

export default servicesSlice.reducer;